/** Public Explore gallery client — no auth (guest-browsable marketplace). */
const API = "/api/v1";

export interface ExploreCreator {
  name: string;
  slug: string;
  logo: string | null;
  count: number;
}

export interface ExploreDesign {
  id: string;
  name: string;
  category: string | null;
  posterUrl: string | null;
  likeCount: number;
  configurable: boolean;
  creator: { name: string; slug: string; logo: string | null } | null;
}

export interface ExploreResult {
  designs: ExploreDesign[];
  creators: ExploreCreator[];
}

export interface ExploreParams {
  category?: string;
  q?: string;
  sort?: "liked" | "newest";
  limit?: number;
  offset?: number;
}

export async function explore(params: ExploreParams = {}): Promise<ExploreResult> {
  const qs = new URLSearchParams();
  if (params.category && params.category.toLowerCase() !== "all") qs.set("category", params.category);
  if (params.q) qs.set("q", params.q);
  if (params.sort) qs.set("sort", params.sort);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const r = await fetch(`${API}/showcase/explore?${qs.toString()}`);
  const j = (await r.json()) as { success: boolean; message?: string; data: ExploreResult };
  if (!r.ok || !j.success) throw new Error(j.message || "Failed to load Explore");
  return j.data;
}

/** Toggle a like. Returns the new count. Client dedups liked-state in localStorage. */
export async function likeDesign(id: string, unlike = false): Promise<number> {
  const r = await fetch(`${API}/showcase/designs/${id}/like`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unlike }),
  });
  const j = (await r.json()) as { success: boolean; data?: { likeCount: number } };
  return j.data?.likeCount ?? 0;
}

const LIKED_KEY = "jewelitt:likedDesigns";

export function getLikedSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]") as string[]);
  } catch {
    return new Set();
  }
}

export function setLiked(id: string, liked: boolean): void {
  const s = getLikedSet();
  if (liked) s.add(id);
  else s.delete(id);
  try {
    localStorage.setItem(LIKED_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode */
  }
}
